/**
 * 🗓 분기 재고 실사 — 분기 고르기 + 한 품목씩 확인 (v2.224.0 · 2026-10-01 · 사용자 요청)
 *
 * 사용자 원문(개발 계획 › 재고관리): 「SM재고 앱에서 분기재고실사 시작 버튼 … 해당연도 몇분기 … 이미 완료된건 완료됬다고
 *   표시하고, 1~4분기까지 … 매주 하드디스크 재고실사 하는것처럼 하나씩 수량 보여주고 진행 … 잔여재고가 남아있는 것들 …
 *   항목마다 사진 … 무시하고 넘어가기 … 임시저장 … 우선넘기고 나중에 다시 확인 … 모두 끝나면 분기재고실사 완료 보고서」
 *   10-01: 「장소에 몰려있는것끼리 숫자 세려다보면 안내해주는 순서와 안맞을 수 있으니까 안맞으면 그냥 넘기기 로 넘기고,
 *   나중에 다시 숫자 센다는 의미였어」 → [⏳ 나중에] 가 그것. 장소 자료는 만들지 않는다.
 *
 * 두 화면
 *   · /m/stock-audit        — 그 해 1~4분기 카드(세는 중 · 확인 끝 · 보고서 결재 중 · 완료 · 아직 안 함) + [▶ 실사 시작]
 *   · /m/stock-audit/:id    — card(하나씩) · list(목록) · done(마침 + 📄 보고서 상신)
 *
 * ⭐임시저장 단추가 없는 이유 — 품목 하나 누를 때마다 **바로 저장**된다(주간 확인과 같다). 앱을 닫았다 와도 그 자리.
 * ⭐네 단추 — ✅ 맞음(장부 그대로) · ✏️ 다름(센 수·메모) · ⏭ 무시(사유) · ⏳ 나중에(판정 아님 · 줄만 뒤로).
 *   「다음」은 아직 안 센 품목 → 그다음 「나중에」 품목 순서로 간다. 목록에서 아무 품목이나 눌러 바로 갈 수도 있다.
 * ⭐잠김(보고서 결재 중·최종승인)·진행률·보고서 가능 여부는 **서버가 판정**해 내려 준다 — 화면은 그대로 그린다.
 * ⭐재고 장부는 이 화면 어디서도 바뀌지 않는다 — 「다름」은 기록이다.
 * ⚠조회전용(viewer)은 보기만(단추를 감추고 서버도 막는다).
 * ⭐[✖ 실사 취소](v2.225.0 · 관리자급) — 분기 카드·목록·마침 화면. 기록·사진(NAS 파일까지) 모두 지우고 같은 분기를 다시 시작할 수 있다.
 *   확인 창은 lib/stock-audit-cancel.ts(PC [분기 실사] 탭과 같은 것). 보고서가 붙은 실사엔 단추가 없다.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import * as api from '../lib/stock-audit-api';
import * as stockApi from '../lib/stock-api';
import type { AuditDetail, AuditItem, AuditItemStatus, AuditSummary } from '../lib/stock-audit-api';
import { MobileAppTabs } from '../components/MobileAppTabs';
import { CountCalculator } from '../components/CountCalculator';
import { StockPhotoStrip } from '../components/StockPhotoStrip';
import { CALC_EMPTY, calcCompare, calcEvaluate } from '../lib/count-calc';
import type { CalcState } from '../lib/count-calc';
import { timeLabel, kstDate, dateTimeLabel } from '../lib/date-label';
import { cancelDoneText, confirmCancelAudit } from '../lib/stock-audit-cancel';
import { LocationQtyFields } from '../components/LocationQtyFields';
import { draftFrom, locText, parseDraft, sameDraft } from '../lib/stock-locations';
import type { LocDraft } from '../lib/stock-locations';
import { LoadingLines } from '../components/ui/Skeleton';

type View = 'card' | 'list' | 'done';
type Tone = 'amber' | 'emerald' | 'red' | 'blue';
type ListFilter = 'all' | 'left' | 'later' | 'diff' | 'skip';

const BAND_TONE: Record<Tone, string> = {
  amber: 'bg-amber-50 border-amber-300 text-amber-900',
  emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  red: 'bg-red-50 border-red-300 text-red-800',
  blue: 'bg-blue-50 border-blue-300 text-blue-900',
};

const STATUS_ICON: Record<AuditItemStatus, string> = { pending: '⬜', later: '⏳', ok: '✅', diff: '⚠️', skip: '⏭' };
const STATUS_LABEL: Record<AuditItemStatus, string> = { pending: '미확인', later: '나중에', ok: '맞음', diff: '다름', skip: '무시' };

/** 실제 수량 칸 — 비었거나 0 이상 정수(서버 상한 1,000,000 안) */
const QTY_RE = /^\d{1,6}$/;

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '-';
  return n.toLocaleString('ko-KR');
}

function whenLabel(iso: string | null | undefined, today: string): string {
  if (!iso) return '';
  return today && kstDate(iso) === today ? timeLabel(iso) : dateTimeLabel(iso);
}

function nameOf(it: AuditItem): string {
  return it.itemName ?? it.itemCode;
}

function Band({ tone, children, onClose }: { tone: Tone; children: ReactNode; onClose?: () => void }) {
  return (
    <div className={`border-b px-3 py-2 text-xs flex items-start gap-2 ${BAND_TONE[tone]}`}>
      <div className="flex-1 min-w-0 break-words">{children}</div>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="알림 닫기"
          className="shrink-0 -my-1 -mr-1 w-8 h-8 flex items-center justify-center rounded active:bg-black/5">✕</button>
      )}
    </div>
  );
}

/** 🧮 계산기 답이 현재고와 다른데 [맞음]을 누르면 한 번 묻는다(주간 확인과 같은 규칙) */
function confirmAgainstCalc(open: boolean, state: CalcState | undefined, ref: number): boolean {
  if (!open || !state) return true;
  const c = calcCompare(calcEvaluate(state.expr), ref);
  if (c.kind !== 'less' && c.kind !== 'more') return true;
  return window.confirm(`계산기 답 ${fmt(c.value)}개 · 현재고 ${fmt(ref)}개 — 서로 다릅니다.\n그래도 맞다고 적을까요?\n\n다르면 [취소]를 누르고 계산기 아래 노란 단추로 센 수량을 넘겨 주세요.`);
}

function useHeader(title: string) {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-20 bg-slate-900 text-white px-3 py-2 shadow">
      <div className="flex items-center justify-between gap-2">
        <h1 className="sr-only">{title}</h1>
        <MobileAppTabs />
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-300 truncate max-w-[6rem]">{user?.name ?? user?.userId ?? ''}</span>
          <Link to="/m/inventory" className="px-2 py-1 bg-slate-700 active:bg-slate-600 rounded text-[11px] whitespace-nowrap">재고 목록</Link>
        </div>
      </div>
    </header>
  );
}

/* ══════════════════════════════════════════════════
 * 분기 고르기
 * ══════════════════════════════════════════════════ */

const STATE_TONE: Record<AuditSummary['state'], string> = {
  counting: 'bg-blue-600 text-white',
  counted: 'bg-lime-100 text-lime-900 border border-lime-300',
  reviewing: 'bg-amber-100 text-amber-900 border border-amber-300',
  closed: 'bg-emerald-100 text-emerald-900 border border-emerald-300',
};

export function MobileStockAuditList() {
  const header = useHeader('분기 재고 실사');
  const nav = useNavigate();
  const qc = useQueryClient();
  const [year, setYear] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['stock-audit', 'list', year],
    queryFn: () => api.listAudits(year ?? undefined),
    staleTime: 0,
  });
  const d = q.data;
  const y = year ?? d?.year ?? new Date().getFullYear();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 실사 화면에서 [실사 취소] 뒤 넘어온 알림(nav state) — 여기서 취소해도 같은 띠를 쓴다
  const loc = useLocation();
  const [done, setDone] = useState<string | null>(() => (loc.state as { notice?: string } | null)?.notice ?? null);

  async function cancel(a: AuditSummary) {
    if (busy) return;
    if (!confirmCancelAudit({ label: a.label, decided: a.progress.decided, photos: a.photoCount, adhoc: api.isAdhoc(a) })) return;
    setBusy(true);
    setErr(null);
    setDone(null);
    try {
      const r = await api.cancelAudit(a.id);
      setDone(cancelDoneText(r, api.isAdhoc(a)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      await qc.invalidateQueries({ queryKey: ['stock-audit'] });
      setBusy(false);
    }
  }

  async function start(quarter: number) {
    if (!d || busy) return;
    const label = `${y}년 ${quarter}분기`;
    const ok = window.confirm(
      `${label} 재고 실사를 시작합니다.\n\n지금 현재고가 있는 품목 전부가 대상이 되고, 시작하는 순간 목록이 고정됩니다.\n품목마다 바로 저장되니 중간에 닫았다가 이어서 할 수 있습니다.`,
    );
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.startAudit(y, quarter);
      await qc.invalidateQueries({ queryKey: ['stock-audit'] });
      nav(`/m/stock-audit/${r.id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // 🔎 수시 실물확인(v2.324.0)은 분기 칸에 안 들어간다 — 분기 칸은 kind=quarter 만(수시 줄은 quarter 가 없다)
  const byQuarter = new Map((d?.audits ?? []).filter((a) => !api.isAdhoc(a)).map((a) => [a.quarter, a]));
  const adhocs = (d?.audits ?? []).filter((a) => api.isAdhoc(a));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {header}
      <div className="bg-white border-b border-slate-200 px-3 py-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-slate-800">🗓 분기 재고 실사</span>
        <div className="flex items-center gap-1 text-sm">
          <button type="button" onClick={() => setYear(y - 1)} className="w-9 h-9 rounded border border-slate-300 bg-white active:bg-slate-100" aria-label="이전 해">‹</button>
          <span className="font-bold tabular-nums w-14 text-center">{y}년</span>
          <button type="button" onClick={() => setYear(y + 1)} className="w-9 h-9 rounded border border-slate-300 bg-white active:bg-slate-100" aria-label="다음 해">›</button>
        </div>
      </div>
      {d?.owner && (
        <div className="bg-slate-100 border-b border-slate-200 px-3 py-1.5 text-[11px] text-slate-600">
          실사 담당 {d.owner.label} · 지금 {d.current.label}
          {d.startBlockedBy && (
            <span className="text-amber-800"> · {d.startBlockedBy.label} 세는 중이라 새 실사·확인은 그것을 끝낸 뒤에</span>
          )}
        </div>
      )}
      {err && <Band tone="red" onClose={() => setErr(null)}>{err}</Band>}
      {done && <Band tone="emerald" onClose={() => setDone(null)}>✅ {done}</Band>}
      {q.isError && <Band tone="red">불러오지 못했습니다 — {(q.error as Error).message}</Band>}

      <main className="flex-1 px-3 py-3 pb-6 space-y-2.5">
        {q.isLoading && <div className="text-center text-slate-400 py-10 text-sm"><LoadingLines /></div>}
        {d && [1, 2, 3, 4].map((quarter) => {
          const a = byQuarter.get(quarter);
          const future = y > d.current.year || (y === d.current.year && quarter > d.current.quarter);
          const isNow = y === d.current.year && quarter === d.current.quarter;
          return (
            <section key={quarter} className={`bg-white rounded shadow-sm px-4 py-3 ${future ? 'opacity-60' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-base font-bold text-slate-900">
                  {quarter}분기 {isNow && <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded ml-1">지금</span>}
                </div>
                {a ? (
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATE_TONE[a.state]}`}>
                    {a.state === 'closed' ? '✓ 완료' : a.stateLabel}
                  </span>
                ) : future ? (
                  <span className="text-[11px] text-slate-400">아직 오지 않은 분기</span>
                ) : (
                  <span className="text-[11px] text-slate-400">아직 안 함</span>
                )}
              </div>
              {a ? (
                <>
                  <div className="mt-1.5 text-[11px] text-slate-500">
                    {dateTimeLabel(a.startedAt)} {a.startedByName} 시작 · 대상 {a.scopeCount}종
                    {a.report && <span> · 보고서 {a.report.docNo} ({a.report.status})</span>}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500" style={{ width: a.progress.total ? `${Math.min(100, (a.progress.decided / a.progress.total) * 100)}%` : '0%' }} />
                    </div>
                    <span className="text-xs text-slate-600 whitespace-nowrap tabular-nums">
                      <b className="text-slate-900">{a.progress.decided}</b> / {a.progress.total}
                    </span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    맞음 {a.progress.ok} · <span className={a.progress.diff ? 'text-amber-700 font-semibold' : ''}>다름 {a.progress.diff}</span> · 무시 {a.progress.skip}
                    {a.progress.later > 0 && <span> · 나중에 {a.progress.later}</span>}
                  </div>
                  <Link
                    to={`/m/stock-audit/${a.id}`}
                    className={`block mt-2.5 min-h-[44px] rounded text-sm font-bold text-center leading-[44px] ${
                      a.state === 'counting' ? 'bg-blue-600 text-white active:bg-blue-700' : 'bg-slate-800 text-white active:bg-slate-700'
                    }`}
                  >
                    {a.state === 'counting' ? (a.progress.decided > 0 ? '이어서 하기 →' : '시작하기 →')
                      : a.state === 'counted' ? '결과 보기 · 보고서 →' : '결과 보기 →'}
                  </Link>
                  {d.canDelete && !a.report && (
                    <button
                      type="button"
                      onClick={() => void cancel(a)}
                      disabled={busy}
                      className="block w-full mt-2 min-h-[40px] rounded text-xs font-semibold border border-rose-300 bg-white text-rose-700 active:bg-rose-50 disabled:opacity-50"
                    >
                      ✖ 실사 취소 (기록·사진 모두 삭제)
                    </button>
                  )}
                </>
              ) : !future && d.canEdit ? (
                <button
                  type="button"
                  onClick={() => void start(quarter)}
                  disabled={busy || !!d.startBlockedBy}
                  className="block w-full mt-2.5 min-h-[44px] rounded text-sm font-bold bg-emerald-600 text-white active:bg-emerald-700 disabled:opacity-40"
                >
                  {busy ? '시작하는 중…' : '▶ 실사 시작'}
                </button>
              ) : null}
            </section>
          );
        })}
        {d && (
          <AdhocSection d={d} year={y} busy={busy} setBusy={setBusy} setErr={setErr} adhocs={adhocs} onCancel={(a) => void cancel(a)} />
        )}
        <p className="text-[11px] text-slate-400 px-1 pt-1">
          분기 실사 대상은 시작하는 순간 현재고가 있는 품목 전부입니다. 분기 실사·수시 확인 통틀어 한 번에 하나만 셀 수 있고, 다 세면 담당자가 [보고서 상신]으로 결재에 올립니다.
          사진은 NAS 「4. 기술지원부\포털 재고실사 사진」에 보관됩니다.
        </p>
      </main>
    </div>
  );
}

/* ══════════════════════════════════════════════════
 * 🔎 수시 실물확인 (v2.324.0 · 개발요청 #31 권애영)
 *   「연휴 전·연말 등 비정기 시점에 실물 수량을 확인하고 기록·조회」 · 사용자 답 「수시로 하는것은 주간 확인이 주가
 *   될것같습니다. 전체는 분기외에도 필요한경우」 → 시작 때 [골라서]가 먼저, [잔여재고 전체]도 고를 수 있다.
 *   세는 화면은 분기 실사와 같다(한 품목씩 · 맞음·다름·무시·나중에 · 사진 · 장소별 수량 · 메모) · 결재 보고서는 없다.
 * ══════════════════════════════════════════════════ */

function AdhocSection({
  d, year, busy, setBusy, setErr, adhocs, onCancel,
}: {
  d: api.AuditList; year: number; busy: boolean; setBusy: (b: boolean) => void; setErr: (e: string | null) => void;
  adhocs: AuditSummary[]; onCancel: (a: AuditSummary) => void;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const loc = useLocation();
  // 앱 첫 화면 [🔎 수시 실물확인] 이 ?new=adhoc 로 오면 시작 칸을 바로 연다
  const [open, setOpen] = useState(() => new URLSearchParams(loc.search).get('new') === 'adhoc');
  const boxRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (new URLSearchParams(loc.search).get('new') === 'adhoc') boxRef.current?.scrollIntoView({ block: 'start' });
  }, [loc.search]);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(d.today);
  const [mode, setMode] = useState<'pick' | 'all'>('pick');
  const [picked, setPicked] = useState<string[]>([]);
  const [term, setTerm] = useState('');
  const [showZero, setShowZero] = useState(false);
  const stockQ = useQuery({ queryKey: ['stock', 'list'], queryFn: () => stockApi.listStock(), enabled: open, staleTime: 60_000 });
  const rows = useMemo(() => {
    const all = (stockQ.data ?? []).slice().sort((a, b) =>
      Number((b.currentStock ?? 0) > 0) - Number((a.currentStock ?? 0) > 0)
      || (a.displayGroup ?? '').localeCompare(b.displayGroup ?? '') || (a.itemName ?? a.itemCode).localeCompare(b.itemName ?? b.itemCode));
    const words = term.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return all.filter((r) => (showZero || (r.currentStock ?? 0) > 0 || picked.includes(r.itemCode))
      && words.every((w) => `${r.itemName ?? ''} ${r.itemCode} ${r.displayGroup ?? ''}`.toLowerCase().includes(w)));
  }, [stockQ.data, term, showZero, picked]);
  const inStockCount = (stockQ.data ?? []).filter((r) => (r.currentStock ?? 0) > 0).length;
  const titleOk = title.trim().length > 0 && title.trim().length <= 40;
  const canStart = d.canEdit && !d.startBlockedBy && titleOk && !!date && (mode === 'all' || picked.length > 0) && !busy;
  const toggle = (code: string): void => setPicked((p) => (p.includes(code) ? p.filter((c) => c !== code) : [...p, code]));

  async function start() {
    if (!canStart) return;
    const what = mode === 'all' ? `잔여재고 전체(${inStockCount}종)` : `고른 품목 ${picked.length}종`;
    if (!window.confirm(`수시 실물확인을 시작합니다.\n\n이름: ${title.trim()}\n날짜: ${date}\n대상: ${what}\n\n시작하는 순간 대상이 고정되고, 품목마다 바로 저장됩니다(결재 보고서는 없습니다).`)) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.startAdhocAudit({ title: title.trim(), checkDate: date, itemCodes: mode === 'all' ? null : picked });
      await qc.invalidateQueries({ queryKey: ['stock-audit'] });
      nav(`/m/stock-audit/${r.id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section id="adhoc" ref={boxRef} className="bg-white rounded shadow-sm px-4 py-3 scroll-mt-14">
      <div className="flex items-center justify-between gap-2">
        <div className="text-base font-bold text-slate-900">🔎 수시 실물확인</div>
        <span className="text-[11px] text-slate-400">{year}년 {adhocs.length}건</span>
      </div>
      <p className="mt-0.5 text-[11px] text-slate-500">연휴 전·연말 등 필요할 때 — 품목을 골라서(또는 잔여재고 전체) 세고 기록합니다. 결재는 없습니다.</p>

      {adhocs.map((a) => (
        <div key={a.id} className="mt-2.5 border-t border-slate-100 pt-2.5">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-semibold text-slate-900 break-words min-w-0">{a.title ?? a.label} <span className="font-normal text-slate-500 tabular-nums">· {a.checkDate}</span></div>
            <span className={`shrink-0 text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATE_TONE[a.state]}`}>{a.stateLabel}</span>
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            {a.startedByName} 시작 · 대상 {a.scopeCount}종 · <b className="text-slate-800">{a.progress.decided}</b> / {a.progress.total}
            {' '}· 맞음 {a.progress.ok} · <span className={a.progress.diff ? 'text-amber-700 font-semibold' : ''}>다름 {a.progress.diff}</span> · 무시 {a.progress.skip}
            {a.progress.later > 0 && <span> · 나중에 {a.progress.later}</span>}
          </div>
          <div className="mt-2 flex gap-2">
            <Link
              to={`/m/stock-audit/${a.id}`}
              className={`flex-1 min-h-[44px] rounded text-sm font-bold text-center leading-[44px] ${
                a.state === 'counting' ? 'bg-blue-600 text-white active:bg-blue-700' : 'bg-slate-800 text-white active:bg-slate-700'}`}
            >
              {a.state === 'counting' ? (a.progress.decided > 0 ? '이어서 하기 →' : '시작하기 →') : '결과 보기 →'}
            </Link>
            {d.canDelete && (
              <button type="button" onClick={() => onCancel(a)} disabled={busy}
                className="px-3 min-h-[44px] rounded text-xs font-semibold border border-rose-300 bg-white text-rose-700 active:bg-rose-50 disabled:opacity-50">
                ✖ 취소
              </button>
            )}
          </div>
        </div>
      ))}

      {d.canEdit && !open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={busy || !!d.startBlockedBy}
          className="block w-full mt-3 min-h-[44px] rounded text-sm font-bold bg-emerald-600 text-white active:bg-emerald-700 disabled:opacity-40"
        >
          ▶ 수시 실물확인 시작
        </button>
      )}
      {d.canEdit && open && (
        <div className="mt-3 border-t border-slate-200 pt-3 space-y-3">
          <label className="block">
            <span className="block text-xs font-semibold text-slate-700 mb-1">이름</span>
            <input value={title} maxLength={40} onChange={(e) => setTitle(e.target.value)} placeholder="예) 설 연휴 전 확인"
              className="w-full border border-slate-300 rounded px-3 py-2 text-base focus:outline-none focus:border-emerald-500" />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-700 mb-1">확인 날짜</span>
            <input type="date" value={date} max={d.today} onChange={(e) => setDate(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-2 text-base focus:outline-none focus:border-emerald-500" />
          </label>
          <div>
            <span className="block text-xs font-semibold text-slate-700 mb-1">대상</span>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setMode('pick')} aria-pressed={mode === 'pick'}
                className={`min-h-[44px] rounded border text-sm font-semibold ${mode === 'pick' ? 'bg-emerald-50 border-emerald-500 text-emerald-800' : 'bg-white border-slate-300 text-slate-700'}`}>
                골라서{picked.length > 0 ? ` (${picked.length})` : ''}
              </button>
              <button type="button" onClick={() => setMode('all')} aria-pressed={mode === 'all'}
                className={`min-h-[44px] rounded border text-sm font-semibold ${mode === 'all' ? 'bg-emerald-50 border-emerald-500 text-emerald-800' : 'bg-white border-slate-300 text-slate-700'}`}>
                잔여재고 전체{stockQ.data ? ` (${inStockCount})` : ''}
              </button>
            </div>
          </div>
          {mode === 'pick' && (
            <div>
              <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="품목 이름·코드로 찾기"
                className="w-full border border-slate-300 rounded px-3 py-2 text-base focus:outline-none focus:border-emerald-500" />
              <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500">
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={showZero} onChange={(e) => setShowZero(e.target.checked)} /> 현재고 0 품목도 보기
                </label>
                {picked.length > 0 && <button type="button" onClick={() => setPicked([])} className="text-rose-700 underline">고른 것 비우기</button>}
              </div>
              <div className="mt-1.5 max-h-[45vh] overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100">
                {stockQ.isPending ? <div className="p-3"><LoadingLines /></div>
                  : rows.length === 0 ? <div className="p-3 text-xs text-slate-400">맞는 품목이 없습니다</div>
                    : rows.map((r) => (
                      <label key={r.itemCode} className="flex items-start gap-2 px-3 py-2 min-h-[44px] active:bg-slate-50">
                        <input type="checkbox" className="mt-1" checked={picked.includes(r.itemCode)} onChange={() => toggle(r.itemCode)} />
                        <span className="min-w-0 flex-1 text-sm text-slate-800 break-words">
                          {r.itemName ?? r.itemCode}
                          <span className="block text-[11px] text-slate-400">{r.itemCode} · 현재고 {r.currentStock ?? 0}</span>
                        </span>
                      </label>
                    ))}
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button type="button" onClick={() => setOpen(false)} disabled={busy}
              className="px-4 min-h-[48px] rounded border border-slate-300 bg-white text-sm text-slate-700 active:bg-slate-100">접기</button>
            <button type="button" onClick={() => void start()} disabled={!canStart}
              className="flex-1 min-h-[48px] rounded text-sm font-bold bg-emerald-600 text-white active:bg-emerald-700 disabled:opacity-40">
              {busy ? '시작하는 중…'
                : !titleOk ? '이름을 적어 주세요'
                  : mode === 'pick' && picked.length === 0 ? '품목을 골라 주세요'
                    : `▶ 시작 (${mode === 'all' ? `전체 ${inStockCount}` : picked.length}종)`}
            </button>
          </div>
          {d.startBlockedBy && <p className="text-[11px] text-amber-800">{d.startBlockedBy.label} 세는 중이라 그것을 끝낸 뒤에 시작할 수 있습니다.</p>}
        </div>
      )}
    </section>
  );
}

/* ══════════════════════════════════════════════════
 * 한 품목씩 확인
 * ══════════════════════════════════════════════════ */

export function MobileStockAuditRun() {
  const { id } = useParams();
  const auditId = Number(id);
  const { user } = useAuth();
  const header = useHeader('분기 재고 실사');
  const nav = useNavigate();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['stock-audit', 'detail', auditId],
    queryFn: () => api.getAudit(auditId),
    enabled: Number.isFinite(auditId) && auditId > 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const d: AuditDetail | undefined = q.data;
  const items = useMemo<AuditItem[]>(() => d?.items ?? [], [d]);
  const canEdit = d?.canEdit ?? false;
  const today = d?.today ?? '';
  const progress = d?.progress;

  const [idx, setIdx] = useState(0);
  const [view, setView] = useState<View>('card');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: Tone; text: string } | null>(null);
  const [moved, setMoved] = useState<{ itemName: string; seen: number; now: number } | null>(null);
  const [listFilter, setListFilter] = useState<ListFilter>('all');

  // ✏️ 다름 · ⏭ 무시 입력판 — 품목을 옮기면 비운다
  const [diffOpen, setDiffOpen] = useState(false);
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [skipOpen, setSkipOpen] = useState(false);
  const [skipNote, setSkipNote] = useState('');

  // 🧮 계산기 — 기록 가능한 사람에겐 처음부터 펼침(주간 확인 v2.207.1 과 같다)
  const [calcPref, setCalcPref] = useState<boolean | null>(null);
  const calcOpen = calcPref ?? canEdit;
  const [calcByItem, setCalcByItem] = useState<Record<string, CalcState>>({});

  // 📍 장소별 센 수(v2.226.0) — 품목마다. 품목을 옮기면 비우고, 저장된 값(「나중에」 일부 입력 포함)으로 다시 채운다
  const [locState, setLocState] = useState<{ code: string; draft: LocDraft; active: string } | null>(null);
  // 📝 메모 칸(장소 칸이 있을 때 — 다름 입력판 대신)
  const [memoOpen, setMemoOpen] = useState(false);

  // 📄 보고서 특이사항 초안
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [reportBusy, setReportBusy] = useState(false);

  const initRef = useRef<number | null>(null);
  const diffRef = useRef<HTMLDivElement>(null);

  // 앱으로 돌아오면 다시 읽는다(다른 사람이 세고 있을 수 있다)
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') void q.refetch(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [q]);

  // 처음 열 때 한 번 — 다 셌으면 마침, 아니면 첫 미확인(없으면 첫 나중에) 카드
  useEffect(() => {
    if (!d || initRef.current === d.audit.id) return;
    initRef.current = d.audit.id;
    if (d.progress.allDecided) { setView('done'); return; }
    const first = d.items.findIndex((i) => i.status === 'pending');
    const firstLater = d.items.findIndex((i) => i.status === 'later');
    const n = first >= 0 ? first : firstLater;
    setIdx(Math.max(0, n));
    setView('card');
  }, [d]);

  const cur = items[idx] as AuditItem | undefined;

  const countedTrim = counted.trim();
  const countedBad = countedTrim !== '' && !QTY_RE.test(countedTrim);
  const countedNum = countedTrim !== '' && !countedBad ? Number(countedTrim) : null;
  const same = !!cur && countedNum !== null && countedNum === (cur.currentStock ?? 0);
  const diffCanSave = !countedBad && (countedNum !== null || note.trim() !== '') && !same;

  /* 📍 장소별 센 수 (v2.226.0) — 서버가 장소 목록을 주면 「장소 칸 모드」(옛 서버면 예전 다름 입력판) */
  const locations = useMemo(() => d?.locations ?? [], [d]);
  const locMode = locations.length > 0;
  const savedDraft = draftFrom(cur?.locQty ?? null, locations);
  const locDraft: LocDraft = cur && locState?.code === cur.itemCode ? locState.draft : savedDraft;
  const locActive = cur && locState?.code === cur.itemCode ? locState.active : (locations[0]?.key ?? '');
  const locParsed = parseDraft(locDraft, locations);
  /** 적던 수량이 저장된 값과 다르다 — 품목을 옮기면 사라지므로 한 번 묻는다 */
  const locDirty = locMode && !!cur && !sameDraft(locDraft, savedDraft);
  function setLocDraft(next: LocDraft) { if (cur) setLocState({ code: cur.itemCode, draft: next, active: locActive }); }
  function setLocActive(key: string) { if (cur) setLocState({ code: cur.itemCode, draft: locDraft, active: key }); }
  const locLabel = (key: string): string => locations.find((l) => l.key === key)?.label ?? key;

  function clearPanels() {
    setDiffOpen(false); setCounted(''); setNote('');
    setSkipOpen(false); setSkipNote('');
    setMemoOpen(false); setLocState(null);
  }
  /** 카드에서 사람이 옮길 때(← 이전 · 목록 · 다음 →) — 적던 장소 수량이 있으면 한 번 묻는다 */
  function leaveOk(): boolean {
    if (!locDirty) return true;
    return window.confirm('적던 장소별 수량이 아직 저장되지 않았습니다.\n그대로 옮기면 사라집니다 — 남기려면 [취소] 뒤 [⏳ 나중에]를 누르세요.\n\n옮길까요?');
  }
  /**
   * 품목을 옮기거나 화면을 바꾸면 **맨 위로** — 새 품목은 이름·번호부터 보여야 한다.
   * (계산기가 펼쳐진 긴 카드에서 아래쪽에 머문 채 다음 품목이 뜨면, 이름을 못 본 채 숫자판만 보인다.)
   */
  function goto(n: number) {
    clearPanels(); setErr(null); setNotice(null);
    setIdx(Math.max(0, Math.min(items.length - 1, n)));
    setView('card');
    window.scrollTo({ top: 0 });
  }
  function openView(v: View) {
    clearPanels(); setErr(null); setNotice(null);
    setView(v);
    window.scrollTo({ top: 0 });
  }

  /**
   * 저장 뒤 — 지금 자리 **다음**의 미확인 품목 → 없으면 앞쪽 미확인 → 없으면 「나중에」 품목(방금 누른 것 제외) → 없으면 마침.
   * ⭐「나중에」는 미확인을 다 돈 뒤에야 온다 — 장소가 안 맞아 미룬 것을 바로 다시 들이밀지 않는다.
   */
  function advance(list: AuditItem[], justCode: string) {
    const here = list.findIndex((i) => i.itemCode === justCode);
    const from = here === -1 ? idx : here;
    const pick = (st: AuditItemStatus): number => {
      let n = list.findIndex((i, k) => k > from && i.status === st && i.itemCode !== justCode);
      if (n === -1) n = list.findIndex((i, k) => k < from && i.status === st && i.itemCode !== justCode);
      return n;
    };
    let next = pick('pending');
    if (next === -1) next = pick('later');
    if (next === -1) {
      const onlyMe = list.some((i) => i.itemCode === justCode && (i.status === 'later' || i.status === 'pending'));
      if (onlyMe) {
        setNotice({ tone: 'amber', text: '남은 품목이 이것 하나입니다 — 세어서 적어 주세요.' });
        goto(from);
        return;
      }
      openView('done');
      return;
    }
    goto(next);
  }

  async function onFail(e: unknown): Promise<string | null> {
    const msg = e instanceof Error ? e.message : String(e);
    if (e instanceof api.AuditApiError && (e.status === 409 || e.status === 404)) {
      await q.refetch();
      setNotice({ tone: 'amber', text: msg });
      return null;
    }
    return msg;
  }

  async function decide(status: 'ok' | 'diff' | 'skip' | 'later') {
    if (!cur || busy || !canEdit) return;
    const item = cur;
    setBusy(true); setErr(null); setMoved(null);
    try {
      const r = await api.decideItem(auditId, item.itemCode, {
        status,
        countedQty: status === 'diff' ? countedNum : null,
        note: status === 'diff' ? note.trim() || null : status === 'skip' ? skipNote.trim() || null : null,
        seenQty: item.currentStock,
      });
      if (r.moved) setMoved({ itemName: nameOf(item), seen: r.moved.seen, now: r.moved.now });
      const fresh = await q.refetch();
      await qc.invalidateQueries({ queryKey: ['stock-audit', 'list'] });
      advance(fresh.data?.items ?? items, item.itemCode);
    } catch (e) {
      const m = await onFail(e);
      if (m) setErr(m);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 📍 장소별 수량으로 저장 (v2.226.0)
   *   count — 맞음·다름: 한 칸 이상 필수 · 빈 칸 0 · **서버가 합계로 판정**(화면은 미리보기로 status 를 보낼 뿐)
   *   later — 나중에: 적은 장소만 남기고 다음 차례로(일부 입력 보존 · 아무것도 안 적어도 된다)
   * 메모(📝)는 둘 다 함께 저장한다.
   */
  async function saveLoc(kind: 'count' | 'later') {
    if (!cur || busy || !canEdit) return;
    const item = cur;
    const p = locParsed;
    if (p.bad) { setErr(`${p.bad} 칸은 0 이상 정수만 적을 수 있습니다`); return; }
    if (kind === 'count' && !p.any) return;
    setBusy(true); setErr(null); setMoved(null);
    try {
      const r = await api.decideItem(auditId, item.itemCode, {
        status: kind === 'later' ? 'later' : p.sum === (item.currentStock ?? 0) ? 'ok' : 'diff',
        locQty: p.qty,
        // 📝 메모 칸을 안 열었으면 적어 둔 메모를 그대로 둔다(나중에로 남긴 메모가 맞음 저장에 지워지지 않게)
        note: memoOpen ? note.trim() || null : item.note ?? null,
        seenQty: item.currentStock,
      });
      if (r.moved) setMoved({ itemName: nameOf(item), seen: r.moved.seen, now: r.moved.now });
      const fresh = await q.refetch();
      await qc.invalidateQueries({ queryKey: ['stock-audit', 'list'] });
      advance(fresh.data?.items ?? items, item.itemCode);
    } catch (e) {
      const m = await onFail(e);
      if (m) setErr(m);
    } finally {
      setBusy(false);
    }
  }

  async function undo(item: AuditItem) {
    if (busy || !canEdit) return;
    if (!window.confirm(`${nameOf(item)} 의 「${STATUS_LABEL[item.status]}」 기록을 지우고 미확인으로 되돌릴까요?`)) return;
    setBusy(true); setErr(null);
    try {
      await api.undoItem(auditId, item.itemCode);
      await q.refetch();
    } catch (e) {
      const m = await onFail(e);
      if (m) setErr(m);
    } finally {
      setBusy(false);
    }
  }

  async function removeAudit() {
    if (!d || busy) return;
    const photos = d.items.reduce((n, it) => n + it.photoCount, 0);
    if (!confirmCancelAudit({ label: d.audit.label, decided: d.progress.decided, photos, adhoc: api.isAdhoc(d.audit) })) return;
    setBusy(true); setErr(null);
    try {
      const r = await api.cancelAudit(auditId);
      await qc.invalidateQueries({ queryKey: ['stock-audit'] });
      nav('/m/stock-audit', { state: { notice: cancelDoneText(r, api.isAdhoc(d.audit)) } });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      // NAS 오류로 중간에 멈췄으면 사진 장수가 줄었다 — 다시 읽는다
      await q.refetch();
    } finally {
      setBusy(false);
    }
  }

  async function submitReport() {
    if (!d || reportBusy) return;
    const notes = notesDraft ?? '';
    const again = !!d.report && (d.report.status === '반려' || d.report.status === '작성중');
    const ok = window.confirm(
      `${d.audit.label} 재고 실사 보고서를 결재로 올립니다.\n\n맞음 ${d.progress.ok} · 다름 ${d.progress.diff} · 무시 ${d.progress.skip} (대상 ${d.progress.total}종)\n`
      + '상신하면 이 실사는 잠깁니다(품목·사진 수정 불가). 결재선: 담당 → 부서장 → 대표이사.',
    );
    if (!ok) return;
    setReportBusy(true); setErr(null);
    try {
      const r = again && d.report
        ? await api.updateAuditReport(d.report.docId, { notes, submit: true })
        : await api.createAuditReport({ auditId, notes, submit: true });
      setNotice({ tone: 'emerald', text: `보고서를 상신했습니다 — ${r.docNo} (검토대기). 결재는 내 결재함에서 진행됩니다.` });
      setNotesDraft(null);
      await q.refetch();
      await qc.invalidateQueries({ queryKey: ['stock-audit', 'list'] });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setReportBusy(false);
    }
  }

  function addChip(setter: (f: (prev: string) => string) => void, c: string) {
    setter((prev) => {
      const t = prev.trim();
      if (t.includes(c)) return prev;
      return t ? `${t} · ${c}` : c;
    });
  }

  function applyCalcQty(qty: number) {
    if (!cur || !canEdit) return;
    // 📍 장소 칸 모드 — 🧮 답은 고른 칸(파란 테두리)에 들어간다. 저장은 아래 단추로
    if (locMode) {
      setLocDraft({ ...locDraft, [locActive]: String(qty) });
      return;
    }
    setSkipOpen(false);
    setDiffOpen(true);
    setCounted(String(qty));
    setTimeout(() => diffRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0);
  }

  /* ── 공통 조각 ───────────────────────────────── */
  if (!Number.isFinite(auditId) || auditId <= 0) {
    return <div className="min-h-screen bg-slate-50">{header}<div className="p-6 text-center text-sm text-red-600">잘못된 주소입니다.</div></div>;
  }
  if (!d || !progress) {
    // 자료가 아직 없다 — 처음 불러오는 중이거나, 처음부터 실패
    const loadErr = q.isError ? (q.error as Error).message : null;
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        {loadErr ? (
          <>
            <div className="p-6 text-center text-sm text-red-600">{loadErr}</div>
            <div className="px-6"><Link to="/m/stock-audit" className="block text-center text-sm text-blue-700 underline">분기 목록으로</Link></div>
          </>
        ) : (
          <div className="p-6 text-center text-sm text-slate-400"><LoadingLines /></div>
        )}
      </div>
    );
  }
  // 자료는 있는데 다시 읽기가 실패한 경우(앱으로 돌아왔을 때 등) — 띠로만 알린다
  const refetchErr = q.isError ? (q.error as Error).message : null;

  // [✖ 실사 취소] — 목록·마침 화면 맨 아래(관리자급 · 보고서가 없을 때만 서버가 canDelete 를 준다)
  const cancelButton = d.canDelete ? (
    <button type="button" onClick={() => void removeAudit()} disabled={busy}
      className="w-full mt-6 min-h-[44px] rounded text-sm font-semibold border border-rose-300 bg-white text-rose-700 active:bg-rose-50 disabled:opacity-50">
      {busy ? '지우는 중…' : api.isAdhoc(d.audit) ? '✖ 이 수시 실물확인 취소 (기록·사진 모두 삭제)' : '✖ 이 분기 실사 취소 (기록·사진 모두 삭제)'}
    </button>
  ) : null;

  const progressBar = (
    <div className="bg-white border-b border-slate-200 px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-xs mb-1.5">
        <Link to="/m/stock-audit" className="font-semibold text-slate-700">{api.isAdhoc(d.audit) ? '🔎' : '🗓'} {api.headingOf(d.audit)} <span className="text-slate-400 font-normal">‹ 목록</span></Link>
        <span className="text-slate-500 whitespace-nowrap">
          <b className="text-slate-900">{progress.decided}</b> / {progress.total}
          {progress.diff > 0 && <span className="text-amber-600 font-semibold"> · 다름 {progress.diff}</span>}
          {progress.later > 0 && <span className="text-slate-600"> · 나중에 {progress.later}</span>}
        </span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: progress.total ? `${Math.min(100, (progress.decided / progress.total) * 100)}%` : '0%' }} />
      </div>
    </div>
  );

  const bands = (
    <div>
      {refetchErr && <Band tone="red">최신 정보를 불러오지 못했습니다 — {refetchErr}</Band>}
      {d.lock.locked && (
        <Band tone={d.lock.closed ? 'emerald' : 'amber'}>
          {d.lock.closed ? '✅ ' : '🔒 '}{d.lock.reason}
        </Band>
      )}
      {!d.lock.locked && d.report && d.report.status === '반려' && (
        <Band tone="red">
          보고서 {d.report.docNo} 가 반려됐습니다{d.report.rejectReason ? ` — ${d.report.rejectReason}` : ''}. 고친 뒤 마침 화면에서 다시 상신하세요.
        </Band>
      )}
      {notice && <Band tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Band>}
      {moved && (
        <Band tone="amber" onClose={() => setMoved(null)}>
          방금 적은 <b>{moved.itemName}</b> — 화면 {fmt(moved.seen)} · 저장 순간 {fmt(moved.now)} (그 사이 입출고가 있었습니다). 기록엔 저장 순간 장부가 남았습니다
        </Band>
      )}
    </div>
  );

  /* ── 마침 화면 ───────────────────────────────── */
  if (view === 'done' || items.length === 0) {
    const diffs = items.filter((i) => i.status === 'diff');
    const skips = items.filter((i) => i.status === 'skip');
    const rep = d.report;
    const notes = notesDraft ?? '';
    const canSubmit = d.canSubmitReport;
    const showForm = canSubmit && (!rep || rep.status === '반려' || rep.status === '작성중');
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {header}
        {progressBar}
        {bands}
        <main className="flex-1 px-3 py-3 pb-6 space-y-3">
          <div className="bg-white rounded shadow-sm px-4 py-4">
            <div className="text-base font-bold text-slate-900">
              {progress.allDecided ? '✅ 확인 끝' : `아직 ${progress.pending + progress.later}종 남음`}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              대상 {progress.total}종 · {dateTimeLabel(d.audit.startedAt)} {d.audit.startedByName} 시작
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2 text-center">
              {([['맞음', progress.ok, 'text-emerald-700'], ['다름', progress.diff, 'text-amber-700'], ['무시', progress.skip, 'text-slate-600'], ['남음', progress.pending + progress.later, 'text-slate-900']] as const).map(([l, n, cls]) => (
                <div key={l} className="bg-slate-50 rounded py-2">
                  <div className={`text-xl font-bold tabular-nums ${cls}`}>{n}</div>
                  <div className="text-[11px] text-slate-500">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {diffs.length > 0 && (
            <div className="bg-white rounded shadow-sm px-4 py-3">
              <div className="text-xs font-semibold text-amber-800 mb-1.5">⚠ 실제와 다른 품목 {diffs.length}종</div>
              <ul className="space-y-1.5">
                {diffs.map((it) => (
                  <li key={it.itemCode}>
                    <button type="button" onClick={() => goto(items.indexOf(it))} className="w-full text-left text-xs active:bg-slate-50 rounded">
                      <span className="font-medium text-slate-900">{nameOf(it)}</span>
                      <span className="text-slate-600"> — 장부 {fmt(it.systemQty)} → 실제 {it.countedQty === null ? '미기재' : fmt(it.countedQty)}</span>
                      {it.note && <span className="text-slate-500"> · {it.note}</span>}
                      {it.photoCount > 0 && <span className="text-slate-400"> · 📷{it.photoCount}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {skips.length > 0 && (
            <div className="bg-white rounded shadow-sm px-4 py-3">
              <div className="text-xs font-semibold text-slate-600 mb-1.5">⏭ 무시한 품목 {skips.length}종</div>
              <ul className="space-y-1 text-xs text-slate-600">
                {skips.map((it) => (
                  <li key={it.itemCode}>
                    <button type="button" onClick={() => goto(items.indexOf(it))} className="w-full text-left active:bg-slate-50 rounded">
                      {nameOf(it)} <span className="text-slate-400">장부 {fmt(it.systemQty)}{it.note ? ` · ${it.note}` : ''}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 📄 보고서 — 🔎 수시 실물확인은 결재 보고서가 없다(v2.324.0 · 기록·조회만) */}
          {api.isAdhoc(d.audit) ? (
            <div className="bg-white rounded shadow-sm px-4 py-3 text-xs text-slate-600">
              🔎 수시 실물확인은 결재 보고서 없이 기록만 남습니다 — 결과는 PC 재고관리 › [실사·실물확인] 탭에서도 봅니다.
            </div>
          ) : (
          <div className="bg-white rounded shadow-sm px-4 py-4">
            <div className="text-sm font-bold text-slate-900 mb-1">📄 분기 재고 실사 보고서</div>
            {rep ? (
              <div className="text-xs text-slate-700">
                <b>{rep.docNo}</b> · {rep.status} · 작성 {rep.authorName}
                {rep.submittedAt && <span className="text-slate-500"> · 상신 {dateTimeLabel(rep.submittedAt)}</span>}
                <div className="mt-1 text-[11px] text-slate-500">결재 진행·확정본 PDF 는 포털 <b>내 결재함</b> · 사내결재 문서함 [분기 재고 실사] 탭에서 봅니다.</div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">아직 상신하지 않았습니다. 결재선: 담당(실사 담당) → 부서장 → 대표이사.</div>
            )}
            {showForm ? (
              <div className="mt-3">
                <label className="block text-xs font-semibold text-slate-700 mb-1">특이사항 <span className="font-normal text-slate-400">(선택 · 보고서에 그대로 실립니다)</span></label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={4}
                  placeholder={'예)\n창고 B 선반 정리 중이라 일부 품목은 박스 겉면으로 확인\nDDR4 메모리 3개 분실 의심 — 입출고 내역 대조 예정'}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm leading-relaxed focus:outline-none focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={() => void submitReport()}
                  disabled={reportBusy}
                  className="w-full mt-2 min-h-[52px] bg-blue-600 text-white rounded font-bold text-base disabled:opacity-50 active:bg-blue-700"
                >
                  {reportBusy ? '올리는 중…' : rep ? '📤 다시 상신' : '📤 보고서 상신'}
                </button>
                <p className="mt-1.5 text-[11px] text-slate-400">상신하면 이 실사는 잠깁니다 — 품목 판정·사진을 고치려면 회수하거나 반려받은 뒤에.</p>
              </div>
            ) : d.submitHint && !d.lock.locked ? (
              <p className="mt-2 text-[11px] text-slate-500">{d.submitHint}</p>
            ) : null}
          </div>
          )}
          {cancelButton}
        </main>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-3 py-2 flex gap-2" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
          <button type="button" onClick={() => openView('list')} className="flex-1 min-h-[48px] bg-white border border-slate-300 rounded text-sm font-semibold text-slate-700 active:bg-slate-100">목록 보기</button>
          {!progress.allDecided && (
            <button type="button" onClick={() => { const n = items.findIndex((i) => i.status === 'pending' || i.status === 'later'); goto(n < 0 ? 0 : n); }}
              className="flex-1 min-h-[48px] bg-slate-800 text-white rounded text-sm font-semibold active:bg-slate-700">남은 품목 세기 →</button>
          )}
        </div>
      </div>
    );
  }

  /* ── 목록 화면 ───────────────────────────────── */
  if (view === 'list' || !cur) {
    const rows = items
      .map((it, n) => ({ it, n }))
      .filter(({ it }) =>
        listFilter === 'all' ? true
          : listFilter === 'left' ? it.status === 'pending' || it.status === 'later'
            : it.status === listFilter);
    const chips: { k: ListFilter; label: string; n: number }[] = [
      { k: 'all', label: '전체', n: items.length },
      { k: 'left', label: '남은 것', n: progress.pending + progress.later },
      { k: 'later', label: '⏳ 나중에', n: progress.later },
      { k: 'diff', label: '⚠ 다름', n: progress.diff },
      { k: 'skip', label: '⏭ 무시', n: progress.skip },
    ];
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        {header}
        {progressBar}
        {bands}
        <main className="flex-1 px-3 py-3 pb-20">
          <div className="flex gap-1.5 flex-wrap mb-2">
            {chips.map((c) => (
              <button key={c.k} type="button" onClick={() => setListFilter(c.k)}
                className={`px-2.5 min-h-[32px] rounded-full text-[11px] font-semibold border ${
                  listFilter === c.k ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>
                {c.label} {c.n}
              </button>
            ))}
          </div>
          <ul className="space-y-1.5">
            {rows.map(({ it, n }) => (
              <li key={it.itemCode}>
                <button type="button" onClick={() => goto(n)}
                  className="w-full text-left bg-white rounded shadow-sm px-3 py-2.5 flex items-center gap-2 active:bg-slate-100">
                  <span className="text-lg w-6 shrink-0 text-center">{STATUS_ICON[it.status]}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-slate-900 truncate">{nameOf(it)}</span>
                    <span className="block text-[11px] text-slate-400 truncate">
                      {it.displayGroup ?? it.itemGroup ?? '기타'} · 현재고 {fmt(it.currentStock)}
                      {it.status === 'diff' ? ` · 실제 ${it.countedQty === null ? '미기재' : fmt(it.countedQty)}` : ''}
                      {it.status === 'skip' ? ' · 무시' : ''}
                      {/* 📍 「나중에」 일부 입력 — 어디까지 셌는지 */}
                      {it.status === 'later' && it.locQty ? ` · ${locText(it.locQty, locations)} 셈` : ''}
                    </span>
                  </span>
                  {it.photoCount > 0 && <span className="text-[10px] text-slate-500 shrink-0">📷{it.photoCount}</span>}
                </button>
              </li>
            ))}
            {rows.length === 0 && <li className="text-center text-xs text-slate-400 py-8">해당하는 품목이 없습니다.</li>}
          </ul>
          {cancelButton}
        </main>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-3 py-2 flex gap-2" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
          <button type="button" onClick={() => goto(Math.min(idx, items.length - 1))}
            className="flex-1 min-h-[48px] bg-slate-800 text-white rounded font-semibold active:bg-slate-700">하나씩 확인으로</button>
          <button type="button" onClick={() => openView('done')}
            className="px-4 min-h-[48px] bg-white border border-slate-300 rounded text-sm font-semibold text-slate-700 active:bg-slate-100">
            {progress.allDecided ? '📄 보고서' : '마침 화면'}
          </button>
        </div>
      </div>
    );
  }

  /* ── 카드 화면 (하나씩) ───────────────────────── */
  const item = cur;
  const st = item.status;
  const live = item.currentStock ?? 0;
  const startMoved = item.currentStock !== null && item.currentStock !== item.startQty;
  const decidedBadge = st === 'pending' ? null : (
    <div className={`mt-3 rounded px-3 py-2 text-xs break-words border ${
      st === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
        : st === 'diff' ? 'bg-amber-50 border-amber-300 text-amber-900'
          : st === 'skip' ? 'bg-slate-100 border-slate-300 text-slate-700'
            : 'bg-blue-50 border-blue-200 text-blue-900'}`}>
      <div className="font-semibold">
        {STATUS_ICON[st]} {STATUS_LABEL[st]}
        {item.decidedByName && <span className="font-normal"> · {item.decidedByName} {whenLabel(item.decidedAt, today)}</span>}
      </div>
      {st === 'diff' && <div className="mt-0.5">장부 {fmt(item.systemQty)} → 실제 {item.countedQty === null ? '미기재' : fmt(item.countedQty)}</div>}
      {st === 'ok' && item.systemQty !== null && <div className="mt-0.5">장부 {fmt(item.systemQty)} 그대로</div>}
      {st === 'later' && <div className="mt-0.5">미확인 품목을 다 돈 뒤에 다시 나옵니다</div>}
      {/* 📍 장소별(나중에면 「어디까지 셌나」) */}
      {item.locQty && <div className="mt-0.5">{locText(item.locQty, locations)}{st === 'later' ? ' — 여기까지 셈' : ''}</div>}
      {item.note && <div className="mt-0.5">{item.note}</div>}
    </div>
  );
  // 📍 아래 큰 단추 — 합계로 미리 판정(저장 순간 서버가 다시 판정한다)
  const locMatch = locParsed.any && !locParsed.bad && locParsed.sum === live;
  const locPartsText = locations.filter((l) => typeof locParsed.qty[l.key] === 'number').map((l) => fmt(locParsed.qty[l.key]!)).join('+');

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {header}
      {progressBar}
      {bands}
      <main className="flex-1 px-3 py-3 pb-4">
        <div className="bg-white rounded shadow-sm px-4 py-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <span className="text-[11px] font-semibold text-slate-500">
              {item.displayGroup ?? item.itemGroup ?? '기타'} · {idx + 1}번째 / {items.length}
            </span>
            {item.photoCount > 0 && <span className="text-[10px] text-slate-500">📷 {item.photoCount}</span>}
          </div>
          <div className="text-lg font-bold text-slate-900 leading-snug break-words">{nameOf(item)}</div>
          <div className="text-[11px] text-slate-400 font-mono break-all mb-3">{item.itemCode}</div>

          <div className="relative bg-slate-50 rounded py-3 text-center mb-3">
            <div className="text-xs text-slate-500 mb-0.5">현재고</div>
            <div className="text-4xl font-bold text-slate-900 tabular-nums">{fmt(live)}</div>
            {startMoved && (
              <div className="text-[11px] text-amber-700 mt-0.5">시작 때 {fmt(item.startQty)} → 그 사이 입출고 있음</div>
            )}
            <button type="button" onClick={() => setCalcPref(!calcOpen)} aria-pressed={calcOpen}
              className={`absolute right-2 top-2 px-2.5 min-h-[36px] rounded-full border text-xs font-semibold whitespace-nowrap ${
                calcOpen ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-300 text-slate-700'}`}>
              {calcOpen ? '🧮 닫기' : '🧮 계산기'}
            </button>
          </div>

          {/* 📍 장소별 센 수 (v2.226.0) — 10층 공장 · B1 창고. 빈 칸은 0 · 나중에는 적은 곳만 남는다 */}
          {locMode && canEdit && (
            <div className="mb-3">
              <LocationQtyFields
                locations={locations}
                draft={locDraft}
                onChange={setLocDraft}
                active={locActive}
                onActive={setLocActive}
                refQty={live}
                disabled={busy}
                diffHint="아직 안 센 곳이 있으면 [⏳ 나중에] — 적은 수량은 남습니다"
              />
            </div>
          )}

          {calcOpen && (
            <div className="mb-3">
              <CountCalculator
                state={calcByItem[item.itemCode] ?? CALC_EMPTY}
                onChange={(update) => setCalcByItem((prev) => ({ ...prev, [item.itemCode]: update(prev[item.itemCode] ?? CALC_EMPTY) }))}
                refQty={live}
                refLabel="현재고"
                {...(canEdit ? (locMode ? {
                  applyLabel: (n: number) => `↑ ${locLabel(locActive)} 칸에 ${fmt(n)} 넣기`,
                  onApply: applyCalcQty,
                  applyWhenSame: true,
                } : {
                  applyLabel: (n: number) => (diffOpen ? `↓ 실제 수량 칸에 ${fmt(n)} 넣기` : `✏️ 실제 ${fmt(n)}개로 다름 적기`),
                  onApply: applyCalcQty,
                  applyWhenSame: diffOpen,
                }) : {})}
              />
            </div>
          )}

          <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
            <div className="flex justify-between"><dt className="text-slate-500">납품예정</dt><dd className="text-slate-800">{fmt(item.pendingDelivery)}</dd></div>
            <div className="flex justify-between"><dt className="text-slate-500">시작 때 장부</dt><dd className="text-slate-800">{fmt(item.startQty)}</dd></div>
          </dl>
          <p className="mt-1.5 text-[11px] text-slate-400">현재고에는 납품예정으로 잡힌 것도 들어 있습니다(실물이 창고에 있음).</p>

          {decidedBadge}
          {st !== 'pending' && canEdit && (
            <button type="button" onClick={() => void undo(item)} disabled={busy}
              className="w-full mt-2 min-h-[44px] bg-white border border-slate-300 rounded text-sm text-slate-600 disabled:opacity-50 active:bg-slate-100">↩ 되돌리기</button>
          )}

          <StockPhotoStrip
            photoKey={{ scope: 'audit', auditId }}
            itemCode={item.itemCode}
            canEdit={canEdit}
            lockedReason={d.lock.reason}
            me={user ? { userId: user.userId, role: user.role } : null}
            onChanged={() => void q.refetch()}
          />

          {/* 📝 메모 (장소 칸 모드) — 맞음·다름·나중에 어느 쪽으로 저장하든 함께 남는다 */}
          {locMode && memoOpen && canEdit && (
            <div className="mt-3 border-t border-slate-200 pt-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">메모 <span className="font-normal text-slate-400">(선택 · 보고서에 실립니다)</span></label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {d.diffChips.map((c) => (
                  <button key={c} type="button" onClick={() => addChip(setNote, c)}
                    className="px-2.5 min-h-[32px] rounded-full bg-white border border-slate-300 text-[11px] text-slate-700 active:bg-slate-100">{c}</button>
                ))}
              </div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="메모 (선택)"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          )}

          {!locMode && diffOpen && canEdit && (
            <div ref={diffRef} className="mt-3 border-t border-slate-200 pt-3 scroll-mb-28">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                실제로 센 수량 <span className="font-normal text-slate-400">(모르면 비워 두고 메모만)</span>
              </label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={counted}
                onChange={(e) => setCounted(e.target.value)} placeholder={String(live)}
                className={`w-full border rounded px-3 py-2.5 text-base focus:outline-none ${countedBad ? 'border-red-400' : 'border-slate-300 focus:border-amber-500'}`} />
              {countedBad && <p className="text-[11px] text-red-600 mt-1">0 이상 정수만</p>}
              {same && <p className="text-[11px] text-amber-700 mt-1">현재고와 같습니다 — [✅ 맞음]으로 적어 주세요.</p>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {d.diffChips.map((c) => (
                  <button key={c} type="button" onClick={() => addChip(setNote, c)}
                    className="px-2.5 min-h-[32px] rounded-full bg-white border border-slate-300 text-[11px] text-slate-700 active:bg-slate-100">{c}</button>
                ))}
              </div>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="메모 (선택)"
                className="w-full mt-2 border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
            </div>
          )}

          {skipOpen && canEdit && (
            <div className="mt-3 border-t border-slate-200 pt-3">
              <label className="block text-xs font-semibold text-slate-700 mb-1">무시하는 이유 <span className="font-normal text-slate-400">(선택 · 보고서에 실립니다)</span></label>
              <div className="flex flex-wrap gap-1.5 mb-2">
                {d.skipChips.map((c) => (
                  <button key={c} type="button" onClick={() => addChip(setSkipNote, c)}
                    className="px-2.5 min-h-[32px] rounded-full bg-white border border-slate-300 text-[11px] text-slate-700 active:bg-slate-100">{c}</button>
                ))}
              </div>
              <textarea value={skipNote} onChange={(e) => setSkipNote(e.target.value)} rows={2} maxLength={500} placeholder="사유 (선택)"
                className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-slate-500" />
            </div>
          )}

          {err && <div className="mt-3 text-xs text-red-600 break-words">{err}</div>}
        </div>

        <div className="flex items-center justify-between gap-2 mt-3">
          <button type="button" onClick={() => { if (leaveOk()) goto(idx - 1); }} disabled={idx === 0}
            className="px-3 min-h-[40px] bg-white border border-slate-300 rounded text-xs text-slate-700 disabled:opacity-40 active:bg-slate-100">← 이전</button>
          <button type="button" onClick={() => { if (leaveOk()) openView('list'); }}
            className="flex-1 min-h-[40px] bg-white border border-slate-300 rounded text-xs text-slate-700 active:bg-slate-100">목록</button>
          <button type="button" onClick={() => { if (leaveOk()) goto(idx + 1); }} disabled={idx >= items.length - 1}
            className="px-3 min-h-[40px] bg-white border border-slate-300 rounded text-xs text-slate-700 disabled:opacity-40 active:bg-slate-100">다음 →</button>
        </div>
      </main>

      <div className="sticky bottom-0 bg-white border-t border-slate-200 px-3 py-2" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))' }}>
        {!canEdit ? (
          <p className="text-center text-xs text-slate-500 py-2">
            {d.lock.locked ? d.lock.reason : '조회 전용 계정은 기록을 남길 수 없습니다 — 보기만 가능합니다.'}
          </p>
        ) : locMode && !skipOpen ? (
          /* 📍 장소 칸 모드 (v2.226.0) — ⏳ 나중에는 **언제나** 누를 수 있다(이미 나중에인 품목도 · 적은 수량은 남는다) */
          <div className="space-y-2">
            <div className="flex gap-2">
              <button type="button"
                onClick={() => { if (!memoOpen && !note && item.note) setNote(item.note); setMemoOpen(!memoOpen); }}
                disabled={busy} aria-pressed={memoOpen}
                className={`flex-1 min-h-[44px] border rounded text-sm font-semibold disabled:opacity-50 ${
                  memoOpen ? 'bg-amber-50 border-amber-400 text-amber-800' : 'bg-white border-slate-300 text-slate-700 active:bg-slate-100'}`}>📝 메모</button>
              <button type="button" onClick={() => { setDiffOpen(false); setMemoOpen(false); setSkipOpen(true); }} disabled={busy}
                className="flex-1 min-h-[44px] bg-white border border-slate-400 text-slate-700 rounded text-sm font-semibold disabled:opacity-50 active:bg-slate-100">⏭ 무시</button>
              <button type="button" data-later onClick={() => void saveLoc('later')} disabled={busy || !!locParsed.bad}
                className="flex-1 min-h-[44px] bg-white border border-blue-300 text-blue-700 rounded text-sm font-semibold disabled:opacity-50 active:bg-blue-50">⏳ 나중에</button>
            </div>
            <button type="button" data-count onClick={() => void saveLoc('count')} disabled={busy || !locParsed.any || !!locParsed.bad}
              className={`w-full min-h-[52px] rounded font-bold text-base disabled:opacity-50 ${
                !locParsed.any || locParsed.bad ? 'bg-slate-300 text-slate-600'
                  : locMatch ? 'bg-emerald-600 text-white active:bg-emerald-700' : 'bg-amber-500 text-white active:bg-amber-600'}`}>
              {busy ? '저장 중…'
                : locParsed.bad ? '수량 칸을 고쳐 주세요'
                  : !locParsed.any ? '장소별 수량을 적어 주세요'
                    : locMatch ? `✅ 맞음 (${locPartsText}) — 다음 →` : `⚠ 다름 (${fmt(locParsed.sum)}) — 다음 →`}
            </button>
          </div>
        ) : diffOpen ? (
          <div className="flex gap-2">
            <button type="button" onClick={clearPanels} disabled={busy}
              className="px-4 min-h-[52px] bg-white border border-slate-300 rounded text-sm text-slate-700 disabled:opacity-50">취소</button>
            <button type="button" onClick={() => void decide('diff')} disabled={busy || !diffCanSave}
              className="flex-1 min-h-[52px] bg-amber-500 text-white rounded font-bold disabled:opacity-50 active:bg-amber-600">
              {busy ? '저장 중…' : '⚠ 다름으로 적고 다음 →'}
            </button>
          </div>
        ) : skipOpen ? (
          <div className="flex gap-2">
            <button type="button" onClick={clearPanels} disabled={busy}
              className="px-4 min-h-[52px] bg-white border border-slate-300 rounded text-sm text-slate-700 disabled:opacity-50">취소</button>
            <button type="button" onClick={() => void decide('skip')} disabled={busy}
              className="flex-1 min-h-[52px] bg-slate-600 text-white rounded font-bold disabled:opacity-50 active:bg-slate-700">
              {busy ? '저장 중…' : '⏭ 무시하고 다음 →'}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button type="button" onClick={() => { setSkipOpen(false); setDiffOpen(true); setTimeout(() => diffRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 0); }} disabled={busy}
                className="flex-1 min-h-[44px] bg-white border border-amber-400 text-amber-700 rounded text-sm font-semibold disabled:opacity-50 active:bg-amber-50">✏️ 다름</button>
              <button type="button" onClick={() => { setDiffOpen(false); setSkipOpen(true); }} disabled={busy}
                className="flex-1 min-h-[44px] bg-white border border-slate-400 text-slate-700 rounded text-sm font-semibold disabled:opacity-50 active:bg-slate-100">⏭ 무시</button>
              <button type="button" onClick={() => void decide('later')} disabled={busy}
                className="flex-1 min-h-[44px] bg-white border border-blue-300 text-blue-700 rounded text-sm font-semibold disabled:opacity-50 active:bg-blue-50">⏳ 나중에</button>
            </div>
            <button type="button"
              onClick={() => { if (!confirmAgainstCalc(calcOpen, calcByItem[item.itemCode], live)) return; void decide('ok'); }}
              disabled={busy}
              className="w-full min-h-[52px] bg-emerald-600 text-white rounded font-bold text-base disabled:opacity-50 active:bg-emerald-700">
              {busy ? '저장 중…' : st === 'ok' ? '✅ 다시 확인하고 다음 →' : '✅ 맞음 — 다음 →'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
